import React, { useState, useEffect } from 'react';
import { WhatsAppNotificationConfig, CreateWhatsAppNotificationContact } from '../../types/whatsapp';
import whatsappService from '../../services/whatsappService';
import ContactForm from './ContactForm';

interface ConfigurationFormProps {
  config: WhatsAppNotificationConfig | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const ConfigurationForm: React.FC<ConfigurationFormProps> = ({ config, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    notificacionesHabilitadas: true,
    diasNotificacion: '1,2,3,4,5',
    horaInicio: '08:00',
    horaFin: '18:00',
    intervaloMonitoreoMinutos: 5,
    intervaloReporteMinutos: 60,
    zonaHoraria: 'America/Santiago',
    esActiva: false
  });
  
  const [contactos, setContactos] = useState<CreateWhatsAppNotificationContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (config) {
      setFormData({
        nombre: config.nombre,
        notificacionesHabilitadas: config.notificacionesHabilitadas,
        diasNotificacion: config.diasNotificacion,
        horaInicio: config.horaInicio,
        horaFin: config.horaFin,
        intervaloMonitoreoMinutos: config.intervaloMonitoreoMinutos,
        intervaloReporteMinutos: config.intervaloReporteMinutos,
        zonaHoraria: config.zonaHoraria,
        esActiva: config.esActiva
      });
      
      setContactos(config.contactos.map(c => ({
        numeroTelefono: c.numeroTelefono,
        nombre: c.nombre,
        apellido: c.apellido,
        email: c.email,
        esActivo: c.esActivo,
        recibeAlertas: c.recibeAlertas,
        recibeReportes: c.recibeReportes,
        recibeSolicitudesAseo: c.recibeSolicitudesAseo
      })));
    }
  }, [config]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const handleDayChange = (dayValue: string, checked: boolean) => {
    const currentDays = formData.diasNotificacion.split(',').filter(d => d.trim());
    let newDays: string[];
    
    if (checked) {
      newDays = [...currentDays, dayValue].sort((a, b) => parseInt(a) - parseInt(b));
    } else {
      newDays = currentDays.filter(d => d !== dayValue);
    }
    
    setFormData(prev => ({
      ...prev,
      diasNotificacion: newDays.join(',')
    }));
  };

  const handleAddContact = (contact: CreateWhatsAppNotificationContact) => {
    setContactos(prev => [...prev, contact]);
  };

  const handleRemoveContact = (index: number) => {
    setContactos(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdateContact = (index: number, contact: CreateWhatsAppNotificationContact) => {
    setContactos(prev => prev.map((c, i) => i === index ? contact : c));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (config) {
        // Actualizar configuración existente
        await whatsappService.updateConfiguration(config.id, {
          ...formData,
          contactos: contactos.map((c, index) => ({
            id: config.contactos[index]?.id || 0,
            ...c
          }))
        });
      } else {
        // Crear nueva configuración
        await whatsappService.createConfiguration({
          ...formData,
          contactos
        });
      }
      
      onSuccess();
    } catch (err) {
      setError(config ? 'Error actualizando configuración' : 'Error creando configuración');
      console.error('Error saving configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const daysOfWeek = [
    { value: '0', label: 'Domingo' },
    { value: '1', label: 'Lunes' },
    { value: '2', label: 'Martes' },
    { value: '3', label: 'Miércoles' },
    { value: '4', label: 'Jueves' },
    { value: '5', label: 'Viernes' },
    { value: '6', label: 'Sábado' }
  ];

  const selectedDays = formData.diasNotificacion.split(',').filter(d => d.trim());

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {config ? 'Editar Configuración' : 'Nueva Configuración'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          {config ? 'Modifica los parámetros de la configuración existente' : 'Configura los parámetros para las notificaciones WhatsApp'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-4 py-5 sm:px-6">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <svg className="flex-shrink-0 h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Información básica */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700">
                Nombre de la configuración *
              </label>
              <input
                type="text"
                name="nombre"
                id="nombre"
                required
                value={formData.nombre}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Ej: Configuración Principal"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="notificacionesHabilitadas"
                id="notificacionesHabilitadas"
                checked={formData.notificacionesHabilitadas}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="notificacionesHabilitadas" className="ml-2 block text-sm text-gray-900">
                Notificaciones habilitadas
              </label>
            </div>
          </div>

          {/* Horarios */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="horaInicio" className="block text-sm font-medium text-gray-700">
                Hora de inicio *
              </label>
              <input
                type="time"
                name="horaInicio"
                id="horaInicio"
                required
                value={formData.horaInicio}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="horaFin" className="block text-sm font-medium text-gray-700">
                Hora de fin *
              </label>
              <input
                type="time"
                name="horaFin"
                id="horaFin"
                required
                value={formData.horaFin}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Días de la semana */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Días de notificación *
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {daysOfWeek.map((day) => (
                <div key={day.value} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`day-${day.value}`}
                    checked={selectedDays.includes(day.value)}
                    onChange={(e) => handleDayChange(day.value, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`day-${day.value}`} className="ml-2 block text-sm text-gray-900">
                    {day.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Intervalos */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="intervaloMonitoreoMinutos" className="block text-sm font-medium text-gray-700">
                Intervalo de monitoreo (minutos) *
              </label>
              <input
                type="number"
                name="intervaloMonitoreoMinutos"
                id="intervaloMonitoreoMinutos"
                required
                min="1"
                max="60"
                value={formData.intervaloMonitoreoMinutos}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Frecuencia de verificación de alertas (1-60 minutos)
              </p>
            </div>

            <div>
              <label htmlFor="intervaloReporteMinutos" className="block text-sm font-medium text-gray-700">
                Intervalo de reportes (minutos) *
              </label>
              <input
                type="number"
                name="intervaloReporteMinutos"
                id="intervaloReporteMinutos"
                required
                min="1"
                max="1440"
                value={formData.intervaloReporteMinutos}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">
                Frecuencia de envío de reportes (1-1440 minutos)
              </p>
            </div>
          </div>

          {/* Zona horaria */}
          <div>
            <label htmlFor="zonaHoraria" className="block text-sm font-medium text-gray-700">
              Zona horaria
            </label>
            <select
              name="zonaHoraria"
              id="zonaHoraria"
              value={formData.zonaHoraria}
              onChange={handleInputChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="America/Santiago">America/Santiago (Chile Continental)</option>
              <option value="Pacific/Easter">Pacific/Easter (Isla de Pascua)</option>
              <option value="America/Punta_Arenas">America/Punta_Arenas (Magallanes)</option>
            </select>
          </div>

          {/* Calculadora de Mensajes eliminada */}

          {/* Contactos */}
          <div>
            <ContactForm
              contactos={contactos}
              onAddContact={handleAddContact}
              onRemoveContact={handleRemoveContact}
              onUpdateContact={handleUpdateContact}
            />
          </div>

          {/* Activar configuración */}
          {config && (
            <div className="flex items-center">
              <input
                type="checkbox"
                name="esActiva"
                id="esActiva"
                checked={formData.esActiva}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="esActiva" className="ml-2 block text-sm text-gray-900">
                Activar esta configuración
              </label>
              <p className="ml-2 text-xs text-gray-500">
                (Se desactivarán las demás configuraciones)
              </p>
            </div>
          )}
        </div>

        {/* Botones */}
        <div className="mt-8 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {loading ? 'Guardando...' : (config ? 'Actualizar' : 'Crear')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfigurationForm;
